import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Search, AppWindow, X } from "lucide-react";
import { resolveAppIcon } from "../os/icons";

export type PickerItem = {
  /** Stable id — also matched against the query, and used to resolve an icon. */
  id: string;
  name: string;
  subtitle?: string;
  /** Explicit Lucide id; falls back to resolveAppIcon(id, name). */
  icon?: string;
};

type Props = {
  open: boolean;
  items: PickerItem[];
  placeholder?: string;
  emptyText?: string;
  footer?: string;
  ariaLabel?: string;
  /** Max rows shown (list is scrollable, this bounds the DOM). */
  limit?: number;
  onPick: (item: PickerItem) => void;
  onClose: () => void;
};

/**
 * Spotlight-style searchable list. Shared by the menubar app finder and the
 * desktop "Add app / Add widget" flows so they look and behave identically.
 *
 * Rendered through a PORTAL to <body> on purpose. Its callers live inside the
 * menubar, and `.menubar` sets `backdrop-filter` — which makes it the
 * containing block for `position: fixed` descendants. Nested, the full-screen
 * scrim resolved against the 28px menubar instead of the viewport and painted
 * only as tall as its own padding. Portalling puts it back on the viewport.
 */
export function SearchPicker({
  open,
  items,
  placeholder = "Search…",
  emptyText = "No matches",
  footer = "Up/Down navigate · Enter select · Esc close",
  ariaLabel = "Search",
  limit = 12,
  onPick,
  onClose,
}: Props) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const hits = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items.slice(0, limit);
    return items
      .filter(
        (h) => h.name.toLowerCase().includes(query) || h.id.toLowerCase().includes(query)
      )
      .slice(0, limit);
  }, [items, q, limit]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setIdx(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => setIdx(0), [q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (h: PickerItem) => {
    onPick(h);
    onClose();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(Math.max(hits.length - 1, 0), i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hits[idx]) pick(hits[idx]);
    }
  };

  return createPortal(
    <div className="spotlight-backdrop" onMouseDown={onClose}>
      <div
        className="spotlight glass glass-strong"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="spotlight-row">
          <Search size={16} strokeWidth={2} className="spotlight-ico" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="spotlight-input"
            autoComplete="off"
          />
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div className="spotlight-list">
          {!hits.length && <div className="spotlight-empty">{emptyText}</div>}
          {hits.map((h, i) => {
            const Icon = resolveAppIcon(h.id, h.name, h.icon) || AppWindow;
            return (
              <button
                key={h.id}
                type="button"
                className={`spotlight-hit ${i === idx ? "active" : ""}`}
                onMouseEnter={() => setIdx(i)}
                onClick={() => pick(h)}
              >
                <span className="spotlight-hit-ico">
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <span className="spotlight-hit-text">
                  <span className="spotlight-hit-name">{h.name}</span>
                  {h.subtitle && <span className="spotlight-hit-sub">{h.subtitle}</span>}
                </span>
              </button>
            );
          })}
        </div>
        <div className="spotlight-foot">{footer}</div>
      </div>
    </div>,
    document.body
  );
}
